/*
 * Pure shaping and aggregation for the instant-task reporting slice
 * (GET /v2/sales_task_list/instant_report).
 *
 * Its own module for the reason gen_config.ts is: the view renders whatever the
 * server sends, so a field renamed on one side shows up as a column of zeroes
 * rather than an error. Normalising here — every count coerced through one
 * function, every wire field named once — means the contract is asserted by
 * tests instead of by reading the table.
 *
 * Totals are computed client-side from the rows. The contract does carry a
 * `totals` object, but its shape is written as `{ ... }` in the wave file, so
 * summing the rows is the only reading that cannot disagree with the tables
 * directly above it. `totals` from the server is deliberately ignored.
 */

import { dayInTz, resolveFilterTz } from './filter_tz';

export const REPORT_PATH = '/v2/sales_task_list/instant_report';

// The report's date bounds are calendar days in the header timezone (?tz=,
// IST when absent), resolved server-side by the same helper as the list's
// date filter — so the defaults are computed in that zone too.

// Default window: the last 7 days including today, in the selected zone. A week is the shortest
// span that survives a quiet weekend without reading as "nothing is working".
export const REPORT_DEFAULT_DAYS = 7;

// goserver instantRuleRow.
export interface RuleRow {
  task_type: string;
  task_label: string;
  created: number;
  opened: number;
  completed: number;
  auto_completed: number;
  median_time_to_open_seconds: number | null;
}

// goserver instantRepRow.
export interface RepRow {
  ops_uuid: string;
  name: string;
  opened: number;
  completed: number;
  alerts_shown: number;
  alerts_dismissed: number;
}

// goserver instantReport.
export interface InstantReportData {
  by_rule: RuleRow[];
  by_rep: RepRow[];
}

export interface ReportRange {
  from: string;
  to: string;
}

// The endpoint's query params: calendar days (YYYY-MM-DD) in tz.
export interface ReportQuery {
  date_from?: string;
  date_to?: string;
  tz?: string;
}

type WireRow = Record<string, unknown> | null | undefined;

export const defaultReportRange = (tz?: string): ReportRange => ({
  from: dayInTz(tz, -(REPORT_DEFAULT_DAYS - 1)),
  to: dayInTz(tz, 0)
});

// A count off the wire. Anything non-numeric (absent field, null, a string the
// server did not intend) reads as 0 rather than NaN, which would otherwise
// poison every total it is added to.
const countOf = (raw: unknown): number => {
  const n = Number(raw);
  return Number.isFinite(n) ? Math.max(0, Math.trunc(n)) : 0;
};

// Median time-to-open, which is the one nullable number in the payload: a rule
// whose tasks were never opened has no median, and 0 seconds is a real (if
// improbable) value, so absent must stay distinguishable from zero.
const optionalSeconds = (raw: unknown): number | null => {
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

/*
 * by_rule rows, in the shape the table renders. Field names are the contract's:
 * task_type, task_label, created, opened, completed, auto_completed,
 * median_time_to_open_seconds.
 */
export const normalizeRuleRows = (rows: unknown): RuleRow[] =>
  (Array.isArray(rows) ? (rows as WireRow[]) : [])
    .filter((r): r is Record<string, unknown> => !!r && !!r.task_type)
    .map((r) => ({
      task_type: String(r.task_type),
      // The server sends the human label with the row; falling back to the raw
      // type keeps a newly shipped rule readable instead of blank.
      task_label: String(r.task_label || r.task_type),
      created: countOf(r.created),
      opened: countOf(r.opened),
      completed: countOf(r.completed),
      auto_completed: countOf(r.auto_completed),
      median_time_to_open_seconds: optionalSeconds(r.median_time_to_open_seconds)
    }));

/*
 * by_rep rows. The contract gives a rep opened/completed/alerts_shown/
 * alerts_dismissed — NOT created or auto_completed, which are properties of a
 * task's generation and expiry and belong to no rep. The rep table therefore
 * carries the dismiss-without-action pair instead of those two columns.
 */
export const normalizeRepRows = (rows: unknown): RepRow[] =>
  (Array.isArray(rows) ? (rows as WireRow[]) : [])
    .filter((r): r is Record<string, unknown> => !!r && !!r.ops_uuid)
    .map((r) => ({
      ops_uuid: String(r.ops_uuid),
      name: String(r.name || 'Unknown'),
      opened: countOf(r.opened),
      completed: countOf(r.completed),
      alerts_shown: countOf(r.alerts_shown),
      alerts_dismissed: countOf(r.alerts_dismissed)
    }));

export const normalizeReport = (
  payload: { by_rule?: unknown; by_rep?: unknown } | null | undefined
): InstantReportData => {
  const d = payload || {};
  return {
    by_rule: normalizeRuleRows(d.by_rule),
    by_rep: normalizeRepRows(d.by_rep)
  };
};

const sumFields = <F extends string>(
  rows: ReadonlyArray<Partial<Record<F, unknown>>> | null | undefined,
  fields: readonly F[]
): Record<F, number> =>
  (rows || []).reduce<Record<F, number>>(
    (acc, row) => {
      fields.forEach((f) => {
        acc[f] += countOf(row[f]);
      });
      return acc;
    },
    fields.reduce((seed, f) => ({ ...seed, [f]: 0 }), {} as Record<F, number>)
  );

export const RULE_TOTAL_FIELDS = ['created', 'opened', 'completed', 'auto_completed'] as const;
export const REP_TOTAL_FIELDS = [
  'opened',
  'completed',
  'alerts_shown',
  'alerts_dismissed'
] as const;

export type RuleTotals = Record<(typeof RULE_TOTAL_FIELDS)[number], number>;
export type RepTotals = Record<(typeof REP_TOTAL_FIELDS)[number], number>;

export const ruleTotals = (rows: ReadonlyArray<Partial<RuleRow>> | null | undefined): RuleTotals =>
  sumFields(rows, RULE_TOTAL_FIELDS);
export const repTotals = (rows: ReadonlyArray<Partial<RepRow>> | null | undefined): RepTotals =>
  sumFields(rows, REP_TOTAL_FIELDS);

// Empty means "no rows at all". A window whose rows are all zero is NOT empty —
// that is a real answer (the rules ran and produced nothing) and deserves the
// table, not the empty state.
export const isReportEmpty = (
  report: { by_rule?: unknown[]; by_rep?: unknown[] } | null | undefined
): boolean => {
  const r = report || {};
  return (r.by_rule || []).length === 0 && (r.by_rep || []).length === 0;
};

// null (not 0) when the denominator is 0, so the caller can render "—" instead
// of a 0% that reads like a measured result.
export const rate = (part: unknown, whole: unknown): number | null => {
  const p = countOf(part);
  const w = countOf(whole);
  if (w <= 0) return null;
  return p / w;
};

export const formatRate = (value: number | null | undefined): string =>
  value === null || value === undefined ? '—' : `${Math.round(value * 100)}%`;

// Share of created tasks that nobody got to before expiry — the single number
// that says whether the instant tier is being worked.
export const autoCompletedRate = (totals: Partial<RuleTotals> | null | undefined): number | null =>
  rate(totals?.auto_completed, totals?.created);

// Pop-up fatigue, per the PRD's success metric: dismissed / shown.
export const dismissRate = (
  row: Partial<Pick<RepRow, 'alerts_dismissed' | 'alerts_shown'>> | null | undefined
): number | null => rate(row?.alerts_dismissed, row?.alerts_shown);

// "2m 23s" / "1h 04m" / "—". Time-to-open is read at a glance to compare rules,
// so seconds are dropped once the figure runs to hours.
export const formatSeconds = (seconds: unknown): string => {
  if (seconds === null || seconds === undefined || !Number.isFinite(Number(seconds))) return '—';
  const total = Math.max(0, Math.round(Number(seconds)));
  if (total < 60) return `${total}s`;
  const minutes = Math.floor(total / 60);
  if (minutes < 60) return `${minutes}m ${String(total % 60).padStart(2, '0')}s`;
  return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, '0')}m`;
};

// date_from / date_to / tz, exactly as the endpoint reads them (YYYY-MM-DD
// calendar days in tz). Both are required — an unbounded report would scan the whole
// table — so a missing one is left to the caller's guard rather than silently
// omitted here.
export const reportQueryParams = ({
  date_from: dateFrom,
  date_to: dateTo,
  tz
}: ReportQuery = {}): URLSearchParams => {
  const params = new URLSearchParams();
  if (dateFrom) params.set('date_from', dateFrom);
  if (dateTo) params.set('date_to', dateTo);
  if (tz) params.set('tz', resolveFilterTz(tz));
  return params;
};

export const reportUrlPath = (range?: ReportQuery): string =>
  `${REPORT_PATH}?${reportQueryParams(range).toString()}`;
